/**
 * tRPC Client for utx-ops-web
 * Connects to the utx-ops-api tRPC endpoints
 */
import { createTRPCReact, httpBatchLink } from '@trpc/react-query';
import { createTRPCProxyClient } from '@trpc/client';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import superjson from 'superjson';
import { getEnv } from '@/config/env';

// Import AppRouter type from the backend
// Note: This type is imported at build time from the backend package
import type { AppRouter } from '../../../utx/src/entry/ops-api/trpc/routers';

/**
 * Create the tRPC React client
 */
export const trpc = createTRPCReact<AppRouter>();

/**
 * Type helpers for inferring input/output types from the router
 */
export type RouterInputs = inferRouterInputs<AppRouter>;
export type RouterOutputs = inferRouterOutputs<AppRouter>;

/**
 * Get the API base URL from env config (same source as REST API calls)
 */
const getBaseUrl = (): string => {
  const url = getEnv('ADMIN_API_URL');
  return url || 'http://localhost:40100';
};

/**
 * Get headers for tRPC requests
 */
const getHeaders = () => {
  const token = localStorage.getItem('token');
  const turnstileToken = localStorage.getItem('turnstileToken');

  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(turnstileToken ? { 'turnstile-token': turnstileToken } : {})
  };
};

/**
 * Create tRPC client configuration
 */
export const createTrpcClient = () => {
  return trpc.createClient({
    transformer: superjson,
    links: [
      httpBatchLink({
        url: `${getBaseUrl()}/trpc`,
        headers: getHeaders
      })
    ]
  });
};

/**
 * Creates a vanilla tRPC client for imperative calls outside React components
 * Called lazily to ensure window.env is available when the client is created
 */
const createVanillaClient = () => createTRPCProxyClient<AppRouter>({
  transformer: superjson,
  links: [
    httpBatchLink({
      url: `${getBaseUrl()}/trpc`,
      headers: getHeaders
    })
  ]
});

// Cached instance - lazily initialized on first access
let _vanillaClientInstance: ReturnType<typeof createVanillaClient> | null = null;

/**
 * Vanilla tRPC client for imperative calls outside React components
 * Usage: await vanillaClient.router.procedure.query/mutate(input)
 * 
 * Note: Lazily initialized to ensure window.env is available
 * when the client is created (not at module initialization time)
 */
export const vanillaClient = new Proxy({} as ReturnType<typeof createVanillaClient>, {
  get(_target, prop) {
    if (!_vanillaClientInstance) {
      _vanillaClientInstance = createVanillaClient();
    }
    return _vanillaClientInstance[prop as keyof typeof _vanillaClientInstance];
  }
});

/**
 * Hook for imperative tRPC queries (e.g., in ProTable request callbacks)
 * Usage: const utils = useTrpcUtils(); await utils.treasury.request.list.fetch({...});
 */
export const useTrpcUtils = () => trpc.useUtils();

// Export AppRouter type for use in components
export type { AppRouter };
